import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../utils/axiosConfig';

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/Components/ui/table";
import { Button } from "@/Components/ui/button";
import { Check, X } from "lucide-react";

const CompanyAccessRequests = () => {
    const { t } = useTranslation();
    const [requests, setRequests] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchRequests = async () => {
        try {
            setIsLoading(true);
            const response = await axiosInstance.get('/user/GetRequestAccess');
            setRequests(response.data);
        } catch (error) {
            console.error('Error fetching access requests:', error);
            setError(t('profile.errorFetchingRequests') || 'Er is een fout opgetreden bij het ophalen van de verzoeken.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchRequests();
    }, []);

    const handleAccept = async (userId) => {
        try {
            await axiosInstance.post(`/User/AcceptAccess/${userId}`);
            // Refresh the list after successful acceptance
            fetchRequests();
        } catch (error) {
            console.error('Error accepting request:', error);
        }
    };

    const handleReject = async (userId) => {
        try {
            await axiosInstance.post(`/User/RejectAccess/${userId}`);
            // Refresh the list after successful rejection
            fetchRequests();
        } catch (error) {
            console.error('Error rejecting request:', error);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('nl-NL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    if (isLoading) return <div className="mt-6 text-center">{t('common.loading')}</div>;
    if (error) return <div className="mt-6 text-red-500">{error}</div>;

    return (
        <div className="mt-6 border-t border-ps-blue-dark pt-4">
            <h3 className="text-lg font-inter text-ps-blue-dark mb-4">
                {t('profile.accessRequests')}
            </h3>

            {requests.length > 0 ? (
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{t('profile.emailAddress')}</TableHead>
                            <TableHead>{t('profile.requestDate')}</TableHead>
                            <TableHead className="text-right">{t('profile.actions')}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {requests.map((request) => (
                            <TableRow key={request.userid}>
                                <TableCell className="font-medium">
                                    {request.email}
                                </TableCell>
                                <TableCell>
                                    {formatDate(request.created)}
                                </TableCell>
                                <TableCell className="text-right">
                                    <div className="flex justify-end gap-2">
                                        <Button
                                            onClick={() => handleAccept(request.userid)}
                                            size="sm"
                                            className="bg-ps-green hover:bg-ps-green/90 text-white"
                                        >
                                            <Check className="h-4 w-4 mr-1" />
                                            {t('profile.accept')}
                                        </Button>
                                        <Button
                                            onClick={() => handleReject(request.userid)}
                                            variant="outline"
                                            size="sm"
                                            className="border-red-500 text-red-500 hover:bg-red-50"
                                        >
                                            <X className="h-4 w-4 mr-1" />
                                            {t('profile.reject')}
                                        </Button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            ) : (
                <div className="text-center py-8 text-gray-500 border rounded-md bg-gray-50">
                    {t('profile.noAccessRequests')}
                </div>
            )}
        </div>
    );
};

export default CompanyAccessRequests;